import { useState, useCallback } from 'react'
import './Minesweeper.css'

type CellState = 'hidden' | 'revealed' | 'flagged'
type GameState = 'playing' | 'won' | 'lost'

interface Cell {
  isMine: boolean
  neighborMines: number
  state: CellState
}

const GRID_SIZE = 9
const MINE_COUNT = 10

function createGrid(): Cell[][] {
  // Initialize empty grid
  const grid: Cell[][] = Array(GRID_SIZE)
    .fill(null)
    .map(() =>
      Array(GRID_SIZE)
        .fill(null)
        .map(() => ({
          isMine: false,
          neighborMines: 0,
          state: 'hidden' as CellState,
        }))
    )

  // Place mines randomly
  let minesPlaced = 0
  while (minesPlaced < MINE_COUNT) {
    const row = Math.floor(Math.random() * GRID_SIZE)
    const col = Math.floor(Math.random() * GRID_SIZE)
    if (!grid[row]![col]!.isMine) {
      grid[row]![col]!.isMine = true
      minesPlaced++
    }
  }

  // Calculate neighbor counts
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      if (!grid[row]![col]!.isMine) {
        let count = 0
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            const nr = row + dr
            const nc = col + dc
            if (
              nr >= 0 &&
              nr < GRID_SIZE &&
              nc >= 0 &&
              nc < GRID_SIZE &&
              grid[nr]![nc]!.isMine
            ) {
              count++
            }
          }
        }
        grid[row]![col]!.neighborMines = count
      }
    }
  }

  return grid
}

export default function Minesweeper() {
  const [grid, setGrid] = useState<Cell[][]>(createGrid)
  const [gameState, setGameState] = useState<GameState>('playing')
  const [flagCount, setFlagCount] = useState(0)

  const resetGame = () => {
    setGrid(createGrid())
    setGameState('playing')
    setFlagCount(0)
  }

  const revealCell = useCallback(
    (row: number, col: number, currentGrid: Cell[][]) => {
      const cell = currentGrid[row]?.[col]
      if (!cell || cell.state !== 'hidden') return currentGrid

      const newGrid = currentGrid.map((r) => r.map((c) => ({ ...c })))
      newGrid[row]![col]!.state = 'revealed'

      // If it's a mine, game over
      if (cell.isMine) {
        // Reveal all mines
        for (let r = 0; r < GRID_SIZE; r++) {
          for (let c = 0; c < GRID_SIZE; c++) {
            if (newGrid[r]![c]!.isMine) {
              newGrid[r]![c]!.state = 'revealed'
            }
          }
        }
        return newGrid
      }

      // If no neighbors, reveal adjacent cells
      if (cell.neighborMines === 0) {
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            const nr = row + dr
            const nc = col + dc
            if (nr >= 0 && nr < GRID_SIZE && nc >= 0 && nc < GRID_SIZE) {
              const result = revealCell(nr, nc, newGrid)
              for (let r = 0; r < GRID_SIZE; r++) {
                for (let c = 0; c < GRID_SIZE; c++) {
                  newGrid[r]![c] = result[r]![c]!
                }
              }
            }
          }
        }
      }

      return newGrid
    },
    []
  )

  const handleCellClick = (row: number, col: number) => {
    if (gameState !== 'playing') return

    const cell = grid[row]?.[col]
    if (!cell || cell.state !== 'hidden') return

    const newGrid = revealCell(row, col, grid)
    setGrid(newGrid)

    // Check for loss
    if (cell.isMine) {
      setGameState('lost')
      return
    }

    // Check for win
    let hiddenCount = 0
    for (let r = 0; r < GRID_SIZE; r++) {
      for (let c = 0; c < GRID_SIZE; c++) {
        if (newGrid[r]![c]!.state === 'hidden' || newGrid[r]![c]!.state === 'flagged') {
          hiddenCount++
        }
      }
    }
    if (hiddenCount === MINE_COUNT) {
      setGameState('won')
    }
  }

  const handleCellRightClick = (e: React.MouseEvent, row: number, col: number) => {
    e.preventDefault()
    if (gameState !== 'playing') return

    const cell = grid[row]?.[col]
    if (!cell || cell.state === 'revealed') return

    setGrid((prev) =>
      prev.map((r, ri) =>
        r.map((c, ci) => {
          if (ri === row && ci === col) {
            const newState = c.state === 'hidden' ? 'flagged' : 'hidden'
            setFlagCount((f) => f + (newState === 'flagged' ? 1 : -1))
            return { ...c, state: newState }
          }
          return c
        })
      )
    )
  }

  const getCellContent = (cell: Cell) => {
    if (cell.state === 'flagged') return '🚩'
    if (cell.state === 'hidden') return ''
    if (cell.isMine) return '💣'
    if (cell.neighborMines === 0) return ''
    return cell.neighborMines.toString()
  }

  return (
    <div className="minesweeper-app">
      <div className="minesweeper-header">
        <div className="minesweeper-counter">{String(MINE_COUNT - flagCount).padStart(3, '0')}</div>
        <button className="minesweeper-reset" onClick={resetGame}>
          {gameState === 'won' ? '😎' : gameState === 'lost' ? '😵' : '🙂'}
        </button>
        <div className="minesweeper-counter">000</div>
      </div>

      <div className="minesweeper-grid">
        {grid.map((row, rowIndex) => (
          <div key={rowIndex} className="minesweeper-row">
            {row.map((cell, colIndex) => (
              <button
                key={colIndex}
                className={`minesweeper-cell ${cell.state} ${
                  cell.state === 'revealed' && cell.isMine ? 'mine' : ''
                } n${cell.neighborMines}`}
                onClick={() => handleCellClick(rowIndex, colIndex)}
                onContextMenu={(e) => handleCellRightClick(e, rowIndex, colIndex)}
                disabled={gameState !== 'playing' && cell.state !== 'revealed'}
              >
                {getCellContent(cell)}
              </button>
            ))}
          </div>
        ))}
      </div>

      {gameState !== 'playing' && (
        <div className="minesweeper-message">
          {gameState === 'won' ? 'YOU WIN!' : 'GAME OVER'}
        </div>
      )}
    </div>
  )
}
